import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Card, CardHead, CardBody, KPI } from "../../Components/CMI/primitives";
import { fmtEur, fmtN, fmtPct } from "../../Components/CMI/charts";
import { I } from "../../Components/CMI/icons";
import PageHead, { SectionHead } from "../../Layouts/CMI/PageHead";
import { getIvghDashboard } from "../../helpers/ivghApi";
import { MESES } from "../../helpers/meses";

const SEMAFORO = {
  azul:  { bg: "#e8f4fd", color: "#0d6efd", label: "Excelente" },
  verde: { bg: "#e8f5e9", color: "#198754", label: "Cumple" },
  ambar: { bg: "#fff8e1", color: "#cc8800", label: "Casi" },
  rojo:  { bg: "#fdecea", color: "#dc3545", label: "No cumple" },
};

const SemaforoBadge = ({ semaforo, size }) => {
  const s = SEMAFORO[semaforo] || SEMAFORO.rojo;
  return (
    <span style={{
      display: "inline-block", padding: size === "lg" ? "4px 14px" : "2px 10px",
      borderRadius: 12, fontSize: size === "lg" ? 13 : 12, fontWeight: 600,
      background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
};

const CumplBar = ({ value }) => {
  const pct = Math.min(value || 0, 150);
  const sem = value >= 115 ? "azul" : value >= 100 ? "verde" : value >= 90 ? "ambar" : "rojo";
  const color = SEMAFORO[sem].color;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: "#e9ecef", position: "relative" }}>
        <div style={{
          width: `${Math.min(pct / 1.5, 100)}%`, height: "100%", borderRadius: 3,
          background: color, transition: "width .3s",
        }} />
        <div style={{
          position: "absolute", left: `${100 / 1.5}%`, top: -2, width: 1, height: 10,
          background: "#aaa",
        }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 42, textAlign: "right" }}>
        {fmtPct(value)}
      </span>
    </div>
  );
};

const DetailMetric = ({ label, value, sub }) => (
  <div style={{ textAlign: "center" }}>
    <div style={{ fontSize: 11, color: "var(--ce-fg-faint)", marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: "var(--ce-fg-faint)" }}>{sub}</div>}
  </div>
);

const GestorDetail = ({ g }) => {
  const sem = SEMAFORO[g.semaforo] || SEMAFORO.rojo;
  return (
    <tr>
      <td colSpan={6} style={{ padding: 0, background: "#fafbfc" }}>
        <div style={{ padding: "16px 20px", borderTop: "1px solid #e9ecef" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 16, marginBottom: 16 }}>
            <DetailMetric label="Categoría" value={g.categoria} sub={`Coef. ${g.coeficiente.toFixed(2)}`} />
            <DetailMetric label="Proyectos" value={g.proyectos_asignados} sub={`Pond: ${g.proyectos_ponderados?.toFixed(1)}`} />
            <DetailMetric label="H. Reales" value={fmtN(g.horas_trabajadas)} />
            <DetailMetric label="H. Teóricas" value={fmtN(g.horas_teoricas)} />
            <DetailMetric label="Importe" value={fmtEur(g.importe_gestionado)} sub={g.importe_con_iva ? `IVA: ${fmtEur(g.importe_con_iva)}` : null} />
            <DetailMetric label="IVGH Objetivo" value={`${fmtEur(g.ivgh_objetivo)}/h`} />
            <DetailMetric label="IVGH Real" value={`${fmtEur(g.ivgh_real)}/h`} />
            <DetailMetric label="IVGH Teórico" value={`${fmtEur(g.ivgh_teorico)}/h`} />
          </div>

          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 16px", borderRadius: 8, background: sem.bg,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div>
                <span style={{ fontSize: 11, color: "var(--ce-fg-faint)" }}>Cumplimiento</span>
                <div style={{ fontSize: 24, fontWeight: 700, color: sem.color }}>{fmtPct(g.cumplimiento)}</div>
              </div>
              <div style={{ width: 160 }}>
                <CumplBar value={g.cumplimiento} />
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: 11, color: "var(--ce-fg-faint)" }}>Devengo bonus</span>
              <div style={{
                fontSize: 20, fontWeight: 700,
                color: g.devengo_bonus >= 100 ? "var(--ce-success)" : g.devengo_bonus > 0 ? "var(--ce-warning)" : "var(--ce-danger)",
              }}>
                {g.devengo_bonus}%
              </div>
            </div>
            <SemaforoBadge semaforo={g.semaforo} size="lg" />
          </div>
        </div>
      </td>
    </tr>
  );
};

const DashboardIvgh = () => {
  document.title = "IVGH | CMI - Grupo Cucalón Estévez";
  const { year } = useSelector((s) => s.Filters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [mes, setMes] = useState(new Date().getMonth());

  const mesesDisponibles = [];
  const now = new Date();
  const maxMes = now.getFullYear() === year ? now.getMonth() : 11;
  for (let m = 0; m <= maxMes; m++) mesesDisponibles.push(m);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getIvghDashboard(year, "all", mes + 1);
      setData(res);
    } catch (e) {
      console.error("[IVGH]", e);
      setError("Error al cargar datos IVGH");
    } finally {
      setLoading(false);
    }
  }, [year, mes]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("cmi:refresh", h);
    return () => window.removeEventListener("cmi:refresh", h);
  }, [load]);

  if (loading && !data) return (
    <div className="page-content">
      <PageHead icon={I.BarChart3} title="IVGH" sub="Importe por Valor y Gestión Horaria" />
      <div style={{ textAlign: "center", padding: 60, color: "var(--ce-fg-faint)" }}>Cargando…</div>
    </div>
  );

  if (error) return (
    <div className="page-content">
      <PageHead icon={I.BarChart3} title="IVGH" sub="Importe por Valor y Gestión Horaria" />
      <div style={{ textAlign: "center", padding: 60, color: "var(--ce-danger)" }}>{error}</div>
    </div>
  );

  if (!data) return null;

  const { ivgh_base, presupuesto_total, horas_previstas_total, gestores = [], equipos = [], periodo, alertas = [] } = data;

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="page-content">
      <PageHead icon={I.BarChart3} title="IVGH" sub="Importe por Valor y Gestión Horaria" />

      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPI tier="hero" tone="primary" icon={I.TrendUp}
          label="IVGH Base" value={`${fmtEur(ivgh_base)}/h`} />
        <KPI tier="primary" tone="cyan" icon={I.Coin}
          label="Presupuesto ventas" value={fmtEur(presupuesto_total)} />
        <KPI tier="primary" tone="success" icon={I.Clock}
          label="Horas previstas" value={fmtN(horas_previstas_total)} />
        <KPI tier="secondary" icon={I.Users}
          label="Gestores activos" value={gestores.length} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
        <select className="sel" value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
          {mesesDisponibles.map((m) => (
            <option key={m} value={m}>{MESES[m]}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: "var(--ce-fg-faint)" }}>
          Periodo: {periodo?.from} → {periodo?.to}
        </span>
      </div>

      {alertas.length > 0 && (
        <Card style={{ marginBottom: 16, border: "1px solid var(--ce-warning)" }}>
          <CardBody>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              <I.Triangle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
              Alertas ({alertas.length})
            </div>
            {alertas.map((a, i) => (
              <div key={i} style={{
                fontSize: 12, padding: "3px 0",
                color: a.tipo === "error" ? "var(--ce-danger)" : a.tipo === "warning" ? "var(--ce-warning)" : "var(--ce-fg-faint)",
              }}>
                {a.tipo === "error" ? "🔴" : a.tipo === "warning" ? "🟡" : "ℹ️"} {a.msg}
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {/* Tabla compacta con detalle expandible */}
      <SectionHead title="Rendimiento por gestor" />
      <Card style={{ marginBottom: 24 }}>
        <CardBody>
          <div style={{ fontSize: 11, color: "var(--ce-fg-faint)", marginBottom: 8 }}>
            Haz clic en un gestor para ver el detalle completo
          </div>
          <table className="tbl" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Gestor</th>
                <th>Importe</th>
                <th>IVGH Real</th>
                <th style={{ width: 180 }}>Cumplimiento</th>
                <th>Bonus</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {gestores.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--ce-fg-faint)" }}>
                  No hay gestores configurados. Vincula empleados en Config. IVGH.
                </td></tr>
              ) : gestores.map((g) => (
                <React.Fragment key={g.usuario_id}>
                  <tr
                    onClick={() => toggle(g.usuario_id)}
                    style={{
                      cursor: "pointer",
                      background: expanded === g.usuario_id ? "#f0f4ff" : undefined,
                      ...(g.es_head_of_operations ? { fontWeight: 600 } : {}),
                    }}
                  >
                    <td style={{ textAlign: "left" }}>
                      <I.Chevron size={12} style={{
                        marginRight: 6, verticalAlign: -1, transition: "transform .2s",
                        transform: expanded === g.usuario_id ? "rotate(180deg)" : "rotate(-90deg)",
                      }} />
                      {g.nombre}
                      {g.es_head_of_operations && (
                        <span style={{ fontSize: 10, marginLeft: 6, color: "var(--ce-primary)", fontWeight: 600 }}>
                          HEAD OPS
                        </span>
                      )}
                    </td>
                    <td>{fmtEur(g.importe_gestionado)}</td>
                    <td style={{ fontWeight: 600 }}>{fmtEur(g.ivgh_real)}/h</td>
                    <td><CumplBar value={g.cumplimiento} /></td>
                    <td style={{
                      fontWeight: 600,
                      color: g.devengo_bonus >= 100 ? "var(--ce-success)" : g.devengo_bonus > 0 ? "var(--ce-warning)" : "var(--ce-danger)",
                    }}>
                      {g.devengo_bonus}%
                    </td>
                    <td><SemaforoBadge semaforo={g.semaforo} /></td>
                  </tr>
                  {expanded === g.usuario_id && <GestorDetail g={g} />}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {/* Bloques de equipo */}
      {equipos.length > 0 && (
        <>
          <SectionHead title="Equipos" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: 16, marginBottom: 24 }}>
            {equipos.map((eq, i) => {
              const sem = SEMAFORO[eq.semaforo_equipo] || SEMAFORO.rojo;
              return (
                <Card key={i}>
                  <CardHead icon={I.Users} title={eq.head_nombre} right={
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: "var(--ce-fg-faint)" }}>{eq.equipo_kronaly}</span>
                      {eq.es_head_of_operations && (
                        <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "var(--ce-primary)", color: "#fff", fontWeight: 600 }}>HEAD OPS</span>
                      )}
                    </div>
                  } />
                  <CardBody>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "var(--ce-fg-faint)" }}>IVGH Real</div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtEur(eq.ivgh_equipo_real)}/h</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "var(--ce-fg-faint)" }}>IVGH Teórico</div>
                        <div style={{ fontSize: 20, fontWeight: 700, opacity: 0.6 }}>{fmtEur(eq.ivgh_equipo_teorico)}/h</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "var(--ce-fg-faint)" }}>Cumplimiento</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: sem.color }}>{fmtPct(eq.cumplimiento_equipo)}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--ce-fg-faint)", marginBottom: 12 }}>
                      <span>H. Reales: {fmtN(eq.total_horas)}</span>
                      <span>H. Teór: {fmtN(eq.total_horas_teoricas)}</span>
                      <span>Importe: {fmtEur(eq.total_importe)}</span>
                      <span>Obj: {fmtEur(eq.ivgh_equipo_objetivo)}/h</span>
                    </div>
                    {Array.isArray(eq.miembros) && eq.miembros.length > 0 && (
                      <table className="tbl" style={{ width: "100%", fontSize: 11 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left" }}>Miembro</th>
                            <th>Importe</th>
                            <th>Horas</th>
                            <th>IVGH</th>
                            <th>Cumpl.</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {eq.miembros.map((m, j) => (
                            <tr key={j}>
                              <td style={{ textAlign: "left" }}>{m.nombre}</td>
                              <td>{fmtEur(m.importe)}</td>
                              <td>{fmtN(m.horas)}</td>
                              <td>{fmtEur(m.ivgh_real)}/h</td>
                              <td>{fmtPct(m.cumplimiento)}</td>
                              <td><SemaforoBadge semaforo={m.semaforo} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <SemaforoBadge semaforo={eq.semaforo_equipo} />
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardIvgh;
